import { useEffect, useState } from 'react';
import { readGps, maxPairwiseDistance, haversineMeters } from '../utils/exifGps';
import { extractStampGps } from '../utils/extractStampGpsClient';

export const MAX_PAIRWISE_METERS = 25;

/**
 * Drives GPS validation for an array of uploaded files. State is keyed by
 * File identity (NOT array index) so removing a middle entry can never
 * leave the survivor's slot holding the removed file's coords.
 *
 * Source chain per file:
 *   1. exifr reads GPSLatitude/GPSLongitude from EXIF. Most modern phones write it.
 *   2. If EXIF is missing (the file was re-encoded by a camera-overlay app
 *      like NoteCam Lite, or stripped by a messaging app), fall back to the
 *      server-side Vision-LLM stamp reader at /api/inspection/extract-stamp-gps.
 *   3. If both fail, the coord is null and the validator reports 'missing'.
 *
 * The pairwise-distance check at the bottom doesn't care about source — it
 * just compares whatever final coords each file ended up with. So a mix of
 * EXIF-coords and stamp-extracted coords participate in the same
 * "all photos within 25 m" rule.
 *
 * @param {Array<{file:File}>} entries  caller-managed list of upload entries.
 * @returns {{
 *   coords: Array<null | undefined | {lat:number, lng:number, source:'exif'|'stamp', accuracy_m?:number|null, note?:string|null}>,
 *   loadingIndices: Set<number>,
 *   extractingIndices: Set<number>,
 *   validation: {
 *     kind: 'empty' | 'loading' | 'extracting' | 'missing' | 'mismatch' | 'ok',
 *     message?: string,
 *     maxMeters?: number,
 *     farthestPair?: [number, number],
 *     offenderIndices?: Set<number>,
 *   },
 * }}
 */
export function useEvidenceGps(entries) {
  // Map<File, 'loading' | 'extracting' | null | {lat,lng,source,...}>.
  // Absence means "not yet seen".
  const [coordsByFile, setCoordsByFile] = useState(() => new Map());

  useEffect(() => {
    const currentFiles = new Set(entries.map((e) => e.file));
    const newFiles = entries.filter((e) => !coordsByFile.has(e.file)).map((e) => e.file);
    const hasRemoved = [...coordsByFile.keys()].some((f) => !currentFiles.has(f));

    // Early return: state already in sync → no changes, no effect work. This
    // breaks the infinite-loop that would otherwise be created by including
    // coordsByFile in deps.
    if (newFiles.length === 0 && !hasRemoved) return;

    // Atomic update: prune removed files AND mark new ones as 'loading'.
    setCoordsByFile((prev) => {
      const next = new Map();
      for (const [file, v] of prev) {
        if (currentFiles.has(file)) next.set(file, v);
      }
      for (const file of newFiles) {
        next.set(file, 'loading');
      }
      return next;
    });

    // Side effect: read EXIF first, then on EXIF-miss fall through to the
    // server stamp extractor. Lifted OUT of the setState updater so the
    // reads only fire once per file (concurrent-mode safety).
    newFiles.forEach((file) => {
      readGps(file).then((exifCoord) => {
        if (exifCoord) {
          // EXIF hit — fast path. Stamp the result with source='exif' so
          // downstream UI can label provenance.
          setCoordsByFile((prev) => {
            if (!prev.has(file)) return prev;
            const next = new Map(prev);
            next.set(file, { ...exifCoord, source: 'exif' });
            return next;
          });
          return;
        }
        // EXIF miss — flip to 'extracting' so the UI can say "reading the
        // stamp" instead of "still loading". Then fire the server call.
        setCoordsByFile((prev) => {
          if (!prev.has(file)) return prev;
          const next = new Map(prev);
          next.set(file, 'extracting');
          return next;
        });
        extractStampGps(file).then((stampCoord) => {
          setCoordsByFile((prev) => {
            if (!prev.has(file)) return prev;
            const next = new Map(prev);
            // stampCoord is null on any failure (no overlay, low accuracy,
            // outside Iran, network down) — that collapses to the existing
            // 'missing' validation state with the existing "use the original
            // file" hint.
            next.set(file, stampCoord || null);
            return next;
          });
        });
      });
    });
  }, [entries, coordsByFile]);

  // Derive flat per-index arrays for the UI by looking up each entry's File.
  // This is what makes middle-remove safe: coords[i] is ALWAYS the coord for
  // entries[i].file, regardless of how entries was reordered.
  const coords = entries.map((e) => {
    const v = coordsByFile.get(e.file);
    if (v === 'loading' || v === 'extracting') return undefined;
    return v;
  });
  const loadingIndices = new Set(
    entries
      .map((e, i) => (coordsByFile.get(e.file) === 'loading' ? i : -1))
      .filter((i) => i >= 0),
  );
  const extractingIndices = new Set(
    entries
      .map((e, i) => (coordsByFile.get(e.file) === 'extracting' ? i : -1))
      .filter((i) => i >= 0),
  );

  let validation;
  if (entries.length === 0) {
    validation = { kind: 'empty' };
  } else if (loadingIndices.size > 0 || coords.some((c) => c === undefined)) {
    // EXIF still being read — usually <100 ms per file. The `coords.some
    // undefined` catch handles the React render that fires BETWEEN entries
    // updating and the effect setting coordsByFile to 'loading'. Without it,
    // that render sees loadingIndices.size===0 AND coords full of undefined,
    // falls all the way through to maxPairwiseDistance(undefined,undefined)
    // and crashes in haversineMeters with "Cannot read properties of
    // undefined (reading 'lat')". (Bug fix: 2026-05-27 — reproduces with a
    // multi-file select via picker or drag-drop.)
    validation = { kind: 'loading' };
  } else if (extractingIndices.size > 0) {
    // EXIF missed; server is reading the stamp from the pixels. Takes ~1-3 s.
    validation = {
      kind: 'extracting',
      message: 'در حال خواندن مهر موقعیت روی تصویر…',
    };
  } else {
    const missingIdx = coords.findIndex((c) => c === null);
    if (missingIdx >= 0) {
      validation = {
        kind: 'missing',
        offenderIndices: new Set(
          coords.map((c, i) => (c === null ? i : -1)).filter((i) => i >= 0),
        ),
        message: `عکس شماره ${toFa(missingIdx + 1)} موقعیت جغرافیایی ندارد. لطفاً نسخهٔ اصلی فایل را از گالری دوربین انتخاب کنید - پیام‌رسان‌هایی مثل واتساپ این داده را حذف می‌کنند. اگر از اپ مهر-روی-عکس استفاده کرده‌اید، عکس واضح‌تری بفرستید که کادر مهر کامل و خوانا باشد.`,
      };
    } else if (entries.length === 1) {
      validation = { kind: 'ok', maxMeters: 0 };
    } else {
      const { maxMeters, farthestPair } = maxPairwiseDistance(coords);
      if (maxMeters > MAX_PAIRWISE_METERS) {
        const [i, j] = farthestPair;
        // Offender = the photo whose AVERAGE distance to the others is
        // highest. With only 2 photos both are offenders (you can't say
        // which is "right"); with 3 the outlier almost always pops out.
        const offenderIndices = pickOffenders(coords, farthestPair);
        validation = {
          kind: 'mismatch',
          maxMeters,
          farthestPair,
          offenderIndices,
          message: offenderIndices.size === 1
            ? `عکس شمارهٔ ${toFa([...offenderIndices][0] + 1)} حدود ${Math.round(maxMeters)} متر از بقیه فاصله دارد (سقف مجاز ${MAX_PAIRWISE_METERS} متر). این عکس را حذف کنید و یک عکس دیگر از همان نقطه بگذارید.`
            : `عکس‌های شمارهٔ ${toFa(i + 1)} و ${toFa(j + 1)} حدود ${Math.round(maxMeters)} متر از هم فاصله دارند (سقف مجاز ${MAX_PAIRWISE_METERS} متر). احتمالاً مربوط به یک نقطه نیستند.`,
        };
      } else {
        validation = { kind: 'ok', maxMeters, farthestPair };
      }
    }
  }

  return { coords, loadingIndices, extractingIndices, validation };
}

/**
 * Pick the photo(s) that are responsible for the mismatch. With 2 photos
 * both members of the farthest pair are offenders. With 3+, compute each
 * point's mean distance to the others — the outlier is the one with the
 * largest mean. If two points tie within 1 m, mark both (rare; usually
 * means the user shot from two different sites and only one matches the
 * majority).
 */
function pickOffenders(coords, farthestPair) {
  const valid = coords.filter(Boolean);
  if (valid.length <= 2) return new Set(farthestPair);
  // Mean distance from each point to the others — the outlier wins.
  const means = coords.map((c, i) => {
    if (!c) return -Infinity;
    let sum = 0; let count = 0;
    for (let j = 0; j < coords.length; j += 1) {
      if (j === i || !coords[j]) continue;
      sum += haversineMeters(c, coords[j]);
      count += 1;
    }
    return count === 0 ? -Infinity : sum / count;
  });
  let maxMean = -Infinity;
  let winnerIdx = -1;
  means.forEach((m, i) => { if (m > maxMean) { maxMean = m; winnerIdx = i; } });
  const offenders = new Set([winnerIdx]);
  means.forEach((m, i) => {
    if (i !== winnerIdx && maxMean - m < 1) offenders.add(i);
  });
  return offenders;
}

function toFa(n) {
  return String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}
